import {useCallback} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {MatchLobbyResponse, WebsocketEvent} from "@devour/client";
import useWebsocket from "@/hooks/useWebsocket";

/**
 * Payload of TOURNAMENT_MATCH_SCORE_UPDATE — broadcast to the tournament room
 * by the backend accumulator after every counted scoring event
 * (`backend/src/services/tournament/TournamentScoreAccumulator.ts`).
 */
export interface TournamentMatchScoreUpdatePayload {
    tournamentId: string;
    /** Bracket match id — the id the match page routes on. */
    matchId: string;
    team1Id: string;
    team2Id: string;
    team1Score: number;
    team2Score: number;
    /** First-to-X target (METRIC_THRESHOLD templates only). */
    targetValue?: number;
    /** Epoch ms when scoring stops (TIME_BASED templates only). */
    scoringEndsAt?: number;
    /** Epoch ms of the increment that produced these totals. */
    updatedAt: number;
}

/**
 * useMatchLiveScore — keeps the match page's running score current between
 * lobby polls. Patches `liveScore` on the `["match-lobby", tournamentId,
 * matchId]` cache in place (prefix key — the real key also carries the
 * viewer's token id), so `useGetMatchLobby` stays the single source of truth
 * and the 20s poll remains the fallback for any missed frame.
 *
 * Room membership is NOT handled here: `useTournamentWebsocket` (mounted on
 * the same page) joins `tournament:{id}`, which is where this event lands.
 */
export function useMatchLiveScore(tournamentId?: string, matchId?: string): void {
    const queryClient = useQueryClient();

    const handleScoreUpdate = useCallback((payload: TournamentMatchScoreUpdatePayload) => {
        if (!tournamentId || !matchId) return;
        if (payload.tournamentId !== tournamentId || payload.matchId !== matchId) return;

        queryClient.setQueriesData<MatchLobbyResponse>(
            {queryKey: ["match-lobby", tournamentId, matchId]},
            (old) => {
                if (!old) return old;
                // Out-of-order frame: never let an older read overwrite a newer one.
                if (old.liveScore && old.liveScore.updatedAt > payload.updatedAt) return old;
                return {
                    ...old,
                    liveScore: {
                        team1Score: payload.team1Score,
                        team2Score: payload.team2Score,
                        targetValue: payload.targetValue,
                        scoringEndsAt: payload.scoringEndsAt,
                        updatedAt: payload.updatedAt,
                    },
                };
            },
        );
    }, [tournamentId, matchId, queryClient]);

    useWebsocket<TournamentMatchScoreUpdatePayload>(WebsocketEvent.TOURNAMENT_MATCH_SCORE_UPDATE, handleScoreUpdate);
}
